import { api, isNetworkError } from "@/lib/api";
import { idb } from "@/lib/offlineDb";
import { answerMatches, prefetchAudio } from "@/lib/speech";

const key = (lang, level, topic) => `${lang}|${level}|${topic}`;

export const saveLesson = (lang, level, topic, data, prefetched = false) =>
  idb.set("lessons", key(lang, level, topic), { ...data, prefetched, saved_at: Date.now() });
export const getLesson = (lang, level, topic) => idb.get("lessons", key(lang, level, topic));
export const saveQuiz = (lang, level, topic, data, prefetched = false) =>
  idb.set("quizzes", key(lang, level, topic), { ...data, prefetched, saved_at: Date.now() });
export const getQuiz = (lang, level, topic) => idb.get("quizzes", key(lang, level, topic));
export const savedLessonKeys = async () => new Set(await idb.keys("lessons"));

// Online: use an unused downloaded copy first, else generate fresh. Offline: use any saved copy.
export async function loadContent({ lang, level, topic, endpoint, get, save }) {
  const cached = await get(lang, level, topic);
  if (cached?.prefetched && navigator.onLine) {
    await save(lang, level, topic, cached, false);
    return cached;
  }
  try {
    const { data } = await api.post(endpoint, { language: lang, level, topic_id: topic });
    await save(lang, level, topic, data);
    return data;
  } catch (e) {
    if (isNetworkError(e) && cached) return { ...cached, fromCache: true };
    if (isNetworkError(e)) throw new Error("You're offline and this hasn't been downloaded yet. Use \"Download next 3 topics\" on your path while online.");
    throw e;
  }
}

export function lessonAudioTexts(l) {
  return [
    ...(l.vocabulary || []).map((v) => v.word),
    ...(l.examples || []).map((e) => e.text),
    ...(l.sounds || []).map((s) => s.example),
    ...(l.characters || []).flatMap((c) => [c.char, c.example]),
    ...(l.grammar_rules || []).map((r) => r.example),
    ...(l.exercises || []).flatMap((x) => [x.audio_text, x.text]),
  ].filter(Boolean);
}

export async function downloadTopics(lang, level, topics, onProgress) {
  const queue = topics.slice(0, 3);
  let step = 0;
  for (const t of queue) {
    onProgress?.(`Saving ${t.title} (${++step}/${queue.length})`);
    const body = { language: lang, level: level.id, topic_id: t.id, prefetch: true };
    if (!(await getLesson(lang, level.id, t.id))?.prefetched) {
      const { data } = await api.post("/lessons/generate", body);
      await saveLesson(lang, level.id, t.id, data, true);
      await prefetchAudio(lessonAudioTexts(data));
    }
    if (!(await getQuiz(lang, level.id, t.id))?.prefetched) {
      const { data } = await api.post("/assessments/generate", body);
      await saveQuiz(lang, level.id, t.id, data, true);
      await prefetchAudio(data.questions.map((q) => q.audio_text).filter(Boolean));
    }
  }
  await api.get(`/review/${lang}`).catch(() => {});
  return queue.length;
}

export function gradeLocally(quiz, answers) {
  const results = quiz.questions.map((q, i) => {
    const k = quiz.answer_key?.[i] || {};
    const correct = answerMatches(answers[i], [k.answer, k.answer_romaji, ...(k.accepted || [])]);
    return { correct, your_answer: answers[i], answer: k.answer_romaji ? `${k.answer} (${k.answer_romaji})` : k.answer,
      prompt: q.prompt, audio_text: q.audio_text, explanation: k.explanation || "" };
  });
  const score = Math.round((100 * results.filter((r) => r.correct).length) / results.length);
  return { score, passed: score >= 70, results };
}

export async function markTopicDoneLocally(lang, levelId, topicId) {
  const k = `GET /progress/${lang}`;
  const map = await idb.get("meta", k);
  const lv = map?.levels?.find((l) => l.id === levelId);
  if (!lv) return;
  if (topicId === "final") lv.final_status = "completed";
  const i = lv.topics.findIndex((t) => t.id === topicId);
  if (i >= 0 && lv.topics[i].status !== "completed") {
    lv.topics[i].status = "completed";
    lv.completed_count += 1;
    if (lv.topics[i + 1]?.status === "locked") lv.topics[i + 1].status = "current";
    if (lv.topics.every((t) => t.status === "completed")) lv.final_status = "current";
  }
  await idb.set("meta", k, map);
}

export async function removeCachedReviewItem(lang, id) {
  const k = `GET /review/${lang}`;
  const deck = await idb.get("meta", k);
  if (!deck) return;
  await idb.set("meta", k, { ...deck, items: deck.items.filter((x) => x.id !== id) });
}
